import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MENU_LOCKED, MENU_BROWSE_LOCKED } from "@/lib/constants";
import { CartProvider } from "@/context/CartContext";
// TV routes stay eager and dependency-light: old Smart TV browsers must parse
// only this chunk (React/Router/axios), never the ERP/Radix/Framer/Recharts
// code that lives in the lazy chunks below.
import TvHome from "@/pages/TvHome";
import TvScreen from "@/pages/TvScreen";

const Toaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const Navbar = lazy(() => import("@/components/layout/Navbar"));
const Footer = lazy(() => import("@/components/layout/Footer"));
const Home = lazy(() => import("@/pages/Home"));
const LocationsPage = lazy(() => import("@/pages/LocationsPage"));
const LocationPage = lazy(() => import("@/pages/LocationPage"));
const MenuPage = lazy(() => import("@/pages/MenuPage"));
const CartPage = lazy(() => import("@/pages/CartPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const ErpLogin = lazy(() => import("@/pages/erp/ErpLogin"));
const ErpApp = lazy(() => import("@/pages/erp/ErpApp"));
const ProyectarLogin = lazy(() => import("@/pages/proyectar/ProyectarLogin"));
const ProyectarApp = lazy(() => import("@/pages/proyectar/ProyectarApp"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

const Layout = () => {
  const { pathname } = useLocation();
  const isErp = pathname.startsWith("/erp") || pathname.startsWith("/proyectar");
  const isTv = pathname.startsWith("/tv");
  return (
    <>
      <ScrollToTop />
      {!isTv && (
        <Suspense fallback={null}>
          {!isErp && <Navbar />}
          <Toaster position="bottom-right" richColors />
        </Suspense>
      )}
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-slate-400">Cargando...</div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/menu" element={MENU_BROWSE_LOCKED ? <Navigate to="/" replace /> : <MenuPage />} />
          <Route path="/carrito" element={MENU_LOCKED ? <Navigate to="/" replace /> : <CartPage />} />
          <Route path="/checkout" element={MENU_LOCKED ? <Navigate to="/" replace /> : <CheckoutPage />} />
          <Route path="/locations" element={<LocationsPage />} />
          <Route path="/locations/:slug" element={<LocationPage />} />
          <Route path="/erp/login" element={<ErpLogin />} />
          <Route path="/erp" element={<ErpApp />} />
          <Route path="/proyectar/login" element={<ProyectarLogin />} />
          <Route path="/proyectar" element={<ProyectarApp />} />
          <Route path="/tv" element={<TvHome />} />
          <Route path="/tv/:code" element={<TvScreen />} />
        </Routes>
      </Suspense>
      {!isErp && !isTv && (
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      )}
    </>
  );
};

function App() {
  useEffect(() => {
    if (window.location.pathname.startsWith("/tv")) return;
    let lenis;
    let rafId;
    let cancelled = false;
    import("lenis").then(({ default: Lenis }) => {
      if (cancelled) return;
      lenis = new Lenis({ lerp: 0.09 });
      window.__lenis = lenis;
      const raf = (time) => {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    });
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (lenis) lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <CartProvider>
          <Layout />
        </CartProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
